/**
 * ARGB color helpers shared by the engine (particle spawning) and the
 * renderer (drawing). Colors are stored as 32-bit ARGB ints, matching the
 * Compose Color encoding used by the original Android implementation.
 */
import { Biome } from './types';

export function argb(a: number, r: number, g: number, b: number): number {
  return (((a & 0xff) << 24) | ((r & 0xff) << 16) | ((g & 0xff) << 8) | (b & 0xff)) >>> 0;
}

export const ARGB_WHITE = argb(0xff, 0xff, 0xff, 0xff);
export const ARGB_BIOME_MEADOW = argb(0xff, 0x90, 0xe8, 0x70);
export const ARGB_BIOME_REEF = argb(0xff, 0x70, 0xd0, 0xff);
export const ARGB_BIOME_LATTICE = argb(0xff, 0xc8, 0x80, 0xff);
export const ARGB_BIOME_MAELSTROM = argb(0xff, 0xff, 0xa0, 0x40);

export const ARGB_FOOD_NORMAL = argb(0xff, 0xff, 0xe6, 0x40);
export const ARGB_FOOD_GOLDEN = argb(0xff, 0xff, 0xd0, 0x00);
export const ARGB_COMBO = argb(0xff, 0xff, 0x60, 0xc0);

export function argbForBiome(b: Biome): number {
  switch (b) {
    case Biome.MEADOW:
      return ARGB_BIOME_MEADOW;
    case Biome.REEF:
      return ARGB_BIOME_REEF;
    case Biome.LATTICE:
      return ARGB_BIOME_LATTICE;
    case Biome.MAELSTROM:
      return ARGB_BIOME_MAELSTROM;
  }
}

import type { Obstacle } from './types';

/** ARGB color for an obstacle's "burst" particles when the snake hits it. */
export function obstacleColorArgb(o: Obstacle): number {
  switch (o.type) {
    case 'GATE':
    case 'PULSE_WALL':
    case 'DOUBLE_GATE':
    case 'SHIFTING_WALL':
      return argb(0xff, 0xff, 0xee, 0xcc);
    case 'LOG':
      return argb(0xff, 0xc8, 0x96, 0x5b);
    case 'BEEHIVE':
      return argb(0xff, 0xff, 0xd0, 0x40);
    case 'KELP_CURTAIN':
      return argb(0xff, 0x60, 0xb0, 0x60);
    case 'JELLYFISH':
      return argb(0xff, 0xff, 0x90, 0xd0);
    case 'CORAL_SPIRE':
      return argb(0xff, 0xff, 0x80, 0x80);
    case 'PORTAL_WALL':
      return argb(0xff, 0xa0, 0x80, 0xff);
    case 'DIAGONAL_BEAM':
      return argb(0xff, 0xff, 0x60, 0xc0);
    case 'CROSS_GATE':
      return argb(0xff, 0xff, 0x60, 0xff);
    case 'CHAOS_RING':
      return argb(0xff, 0xff, 0xb0, 0x40);
    case 'COMET':
      return argb(0xff, 0xff, 0xff, 0x80);
  }
}

export function obstacleRadius(o: Obstacle): number {
  switch (o.type) {
    case 'GATE':
      return o.pillarHeight * 0.5;
    case 'PULSE_WALL':
      return o.totalWidth * 0.5;
    case 'DOUBLE_GATE':
      return o.spacing * 0.5;
    case 'LOG':
      return Math.max(o.width, o.height) * 0.5;
    case 'BEEHIVE':
      return o.ringRadius;
    case 'KELP_CURTAIN':
      return o.totalWidth * 0.5;
    case 'JELLYFISH':
      return 32;
    case 'CORAL_SPIRE':
      return o.height * 0.5;
    case 'PORTAL_WALL':
      return o.totalWidth * 0.5;
    case 'DIAGONAL_BEAM':
      return o.length * 0.5;
    case 'CROSS_GATE':
      return o.armLength;
    case 'SHIFTING_WALL':
      return o.totalWidth * 0.5;
    case 'CHAOS_RING':
      return o.spacing;
    case 'COMET':
      return o.radiusBody + 40;
  }
}

export function obstacleDamage(o: Obstacle): number {
  switch (o.type) {
    case 'CROSS_GATE':
    case 'CHAOS_RING':
      return 2;
    default:
      return 1;
  }
}

/** Mulberry32: small, fast, deterministic PRNG. */
export function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return function () {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
